/** One visibility-aware clock shared by the board and small character portraits. */
export class AnimationClock {
  constructor({
    requestFrame = (callback) => window.requestAnimationFrame(callback),
    cancelFrame = (handle) => window.cancelAnimationFrame(handle),
    visibility = document,
  } = {}) {
    this.requestFrame = (callback) => requestFrame(callback);
    this.cancelFrame = (handle) => cancelFrame(handle);
    this.visibility = visibility;
    this.listeners = new Set();
    this.time = 0;
    this.hotUntil = 0;
    this.frame = 0;
    this.previous = null;
    this.onVisibility = () => {
      if (visibility.hidden) {
        cancelFrame(this.frame);
        this.frame = 0;
        this.previous = null;
      } else this.wake();
    };
    visibility.addEventListener("visibilitychange", this.onVisibility);
    this.tick = this.tick.bind(this);
  }
  subscribe(callback, active = () => true) {
    const listener = { callback, active };
    this.listeners.add(listener);
    this.wake();
    return () => {
      this.listeners.delete(listener);
      if (!this.listeners.size) {
        this.cancelFrame(this.frame);
        this.frame = 0;
        this.previous = null;
      }
    };
  }
  burst(duration = 650) {
    this.hotUntil = Math.max(this.hotUntil, this.time + duration);
    this.wake();
  }
  setHostPaused(paused) {
    this.hostPaused = paused;
    if (paused) {
      this.cancelFrame(this.frame);
      this.frame = 0;
      this.previous = null;
    } else this.wake();
  }
  wake() {
    if (!this.destroyed && !this.hostPaused && !this.frame && !this.visibility.hidden)
      this.frame = this.requestFrame(this.tick);
  }
  tick(timestamp) {
    this.frame = 0;
    if (this.hostPaused || this.visibility.hidden) {
      this.previous = null;
      return;
    }
    const active = [...this.listeners].filter((listener) => listener.active());
    if (!active.length) {
      this.previous = null;
      return;
    }
    const interval = this.time < this.hotUntil ? 1000 / 60 : 1000 / 24;
    if (this.previous === null) this.previous = timestamp - interval;
    const elapsed = timestamp - this.previous;
    if (elapsed >= interval - 1) {
      const delta = Math.min(elapsed, 80);
      this.previous = timestamp;
      this.time += delta;
      for (const listener of active) listener.callback(this.time, delta);
    }
    if (!this.destroyed && !this.hostPaused && !this.visibility.hidden)
      this.frame = this.requestFrame(this.tick);
  }
  destroy() {
    this.destroyed = true;
    this.cancelFrame(this.frame);
    this.visibility.removeEventListener("visibilitychange", this.onVisibility);
    this.listeners.clear();
  }
}
